// src/onboarding/components/PrimaryButton.js

import React from 'react';

/**
 * PRIMARY BUTTON COMPONENT
 * 
 * The main action button that appears at the bottom of every slide.
 * Consistent styling and behavior across all onboarding screens.
 * 
 * HOW TO USE:
 * <PrimaryButton label="Continue" onClick={handleNext} />
 * <PrimaryButton label="Upload Photo" onClick={handleUpload} disabled={!photoSelected} />
 * 
 * PROPS:
 * - label: Button text (default: "Continue")
 * - onClick: Function to call when button is clicked
 * - disabled: Whether button is disabled (default: false)
 * - className: Additional CSS classes (optional)
 * 
 * STYLING:
 * - Full width dark button matching Figma designs
 * - Rounded corners, proper padding for mobile touch
 * - Disabled state with opacity reduction
 * - Smooth transitions for interactions
 */

export default function PrimaryButton({ 
  label = "Continue", 
  onClick, 
  disabled = false, 
  theme = 'light',
  className = "",
  variant = 'solid',
  // Optional leading icon (React node). Will inherit text color via fill-current if provided.
  startIcon = null,
  // Optional trailing icon (React node). Renders to the right of the label.
  endIcon = null,
}) {
  
  // Theme-aware styling
  const buttonStyles = {
    light: {
      background: 'bg-mm_black hover:bg-gray-800',
      text: 'text-white',
      disabled: 'disabled:bg-gray-400',
      focus: 'focus:ring-gray-500'
    },
    dark: {
      background: 'bg-white hover:bg-gray-100',
      text: 'text-mm_black',
      disabled: 'disabled:bg-gray-600',
      focus: 'focus:ring-white'
    }
  };
  
  const currentStyle = buttonStyles[theme];
  const baseClasses = `
    w-full py-3 px-6 
    ${className}
    text-sm font-medium
    rounded-full md:rounded-lg
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    inline-flex items-center justify-center gap-2
  `;

  const variantClasses = variant === 'skip'
    ? `
        bg-white hover:bg-mm_white
        text-mm_black
        border-2 border-mm_black
        disabled:border-gray-300 disabled:text-gray-400 disabled:bg-white disabled:cursor-not-allowed
        focus:ring-mm_black
      `
    : `
        ${currentStyle.background}
        ${currentStyle.disabled} disabled:cursor-not-allowed
        ${currentStyle.text}
        ${currentStyle.focus}
      `;
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses}`.replace(/\s+/g, ' ').trim()}
    >
      {startIcon ? <span aria-hidden="true" className="shrink-0">{startIcon}</span> : null}
      <span>{label}</span>
      {endIcon ? <span aria-hidden="true" className="shrink-0">{endIcon}</span> : null}
    </button>
  );
}